import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useRef, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { BiChevronLeft, BiDollarCircle, BiImage, BiInfoCircle, BiLink, BiPlus, BiTime, BiVideo } from 'react-icons/bi';
import { FaBox, FaCalendarAlt, FaInfo, FaTag } from 'react-icons/fa';
import Skeleton from 'react-loading-skeleton';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

import { useAppSelector } from '@/app/hook';
import { useUploadToCDNMutation } from '@/app/services/chatbot-rtk.services';
import { useGetPackageDetailQuery, useUpdatePackageMutation } from '@/app/services/package-rtk.service';
import { CustomReactMarkdown } from '@/components/common/CustomReactMarkdown';
import BotResponseInput from '@/components/dashboard/knowledge-bases-create/scenario/BotResponseInput';
import { PSButton } from '@/components/ui/button';
import { PSInput } from '@/components/ui/input';
import { MAX_IMAGE_SIZE, MAX_VIDEO_SIZE } from '@/constants/marketplace';
import { selectUseId } from '@/features/auth/auth.slice';
import Step2 from '@/features/scenario/create/components/marketplace/Step2';
import { WrappedErrorBoundary } from '@/pages/WrappedErrorBoundary';
import Utilities from '@/utils';

function PackageDetail() {
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const queryParams = new URLSearchParams(location.search);
  const { t } = useTranslation();
  const package_id = queryParams.get('package_id');
  const { isLoading, isError, data } = useGetPackageDetailQuery(package_id as string);
  const [cdnUpload, cdnUploadResults] = useUploadToCDNMutation();
  const [update, updateResult] = useUpdatePackageMutation();
  const userId = useAppSelector(selectUseId);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const videoInputRef = useRef<HTMLInputElement | null>(null);

  const schema = Yup.object({
    name: Yup.string().required('Name is required'),
    version: Yup.number().required('Version is required').min(1, 'Version must be at least 1'),
    type: Yup.string().required('Type is required').oneOf(['text', 'video'], 'Type must be either text or video'),
    price: Yup.number().required('Price is required').min(0, 'Price must be at least 0'),
    data: Yup.object({
      description: Yup.string().optional(),
      image: Yup.string().url('Image must be a valid URL').optional(),
    }).optional(),
    configuration: Yup.object({
      bots: Yup.array()
        .of(
          Yup.object({
            bot_id: Yup.string().required('Bot ID is required'),
            type: Yup.string().required('Bot type is required'),
          })
        )
        .optional(),
    }).optional(),
  });

  const methods = useForm<any>({
    resolver: yupResolver(schema) as any,
    mode: 'onSubmit',
  });
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
    watch,
    control,
    reset,
  } = methods;
  const onSubmit = (data: any) => {
    data.type = data.type === 'video' ? 'VIDEOBOT' : 'TEXTBOT';

    update({ body: data, package_id: package_id as string })
      .unwrap()
      .then(() => {
        setIsEditing(false);
        toast.success('Package updated successfully');
        navigate(-1);
      })
      .catch(err => {
        toast.error(err?.data?.message || err.message || 'An error occurred');
      });
  };

  const handleUpload = (file: File | null, field: 'promotional_video' | 'image') => {
    setValue(`data.${field}`, null);
    if (file) {
      // File size validation
      if ((field === 'image' && file.size > MAX_IMAGE_SIZE) || (field === 'promotional_video' && file.size > MAX_VIDEO_SIZE)) {
        toast.warn(field === 'image' ? t('marketplace/step3/image/maxFileSize', { max: '8MB' }) : t('marketplace/step3/video/maxFileSize', { max: '45MB' }));
        return;
      }
      const formData = new FormData();
      formData.append('files', file, userId + '_' + file.name);
      formData.append(
        'data',
        JSON.stringify({
          orginalName: file.name,
          type: file.type,
          size: file.size,
        })
      );
      cdnUpload(formData)
        .unwrap()
        .then(value => {
          if (value[0]?.is_successful) {
            const path = value[0].cdnPath;
            setValue(`data.${field}`, path, { shouldValidate: true });
          }
        });
    }
  };

  useEffect(() => {
    if (data) {
      reset({
        name: data.name || '',
        version: data.version,
        type: data.type === 'VIDEOBOT' ? 'video' : 'text',
        price: data.price || 0,
        data: data.data || {},
        configuration: data.configuration || {},
      });
    }
  }, [data, reset]);

  if (isLoading) {
    return (
      <div className="tw-bg-gray-50 tw-min-h-screen tw-p-8">
        <div className="tw-max-w-7xl tw-mx-auto tw-grid tw-grid-cols-1 lg:tw-grid-cols-3 tw-gap-8">
          <div className="lg:tw-col-span-2 tw-space-y-8">
            <Skeleton height={40} width="60%" />
            <Skeleton height={200} />
            <Skeleton height={300} />
          </div>
          <div className="lg:tw-col-span-1 tw-space-y-8">
            <Skeleton height={250} />
            <Skeleton height={150} />
          </div>
        </div>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="tw-bg-gray-50 tw-min-h-screen tw-p-8 tw-flex tw-items-center tw-justify-center">
        <div className="tw-text-center">
          <h2 className="tw-text-2xl tw-font-bold tw-text-red-600 tw-mb-4">{t('packageDetail/failedToLoad')}</h2>
          <p className="tw-text-gray-600">{t('packageDetail/tryAgain')}</p>
        </div>
      </div>
    );
  }

  const { name, version, type, creation_date_time, status } = data;

  return (
    <div className="tw-bg-gray-50 tw-min-h-screen tw-p-4 sm:tw-p-8">
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="tw-max-w-7xl tw-mx-auto tw-grid tw-grid-cols-1 lg:tw-grid-cols-3 tw-gap-8">
            {/* Main Content */}
            <div className="lg:tw-col-span-2 tw-space-y-8">
              {/* Header */}
              <div className="tw-flex tw-flex-row tw-items-center tw-justify-between">
                <div>
                  <PSButton
                    id="btn-detail-back"
                    variant="none"
                    type="button"
                    icon={<BiChevronLeft size={24} />}
                    onClick={() => navigate(-1)}
                    className="tw-flex tw-items-center tw-gap-1 tw-border-none tw-bg-transparent tw-text-gray-700 hover:tw-text-gray-900 tw-pl-0"
                    title={t('Wizard/general/key6')}
                  />
                  <h1 className="tw-text-4xl tw-font-bold tw-text-gray-900 tw-mb-2">{name}</h1>
                  <span className={`tw-px-3 tw-py-1 tw-rounded-full tw-text-xs tw-font-semibold tw-capitalize ${Utilities.renderStatusBadge(status)}`}>
                    {Utilities.renderVersionStatusTitle(status, t)}
                  </span>
                </div>
                {status === 'REJECTED' && (
                  <div className="tw-flex tw-justify-end tw-mb-4 tw-gap-2">
                    {!isEditing ? (
                      <PSButton id="btn-detail-edit" type="button" variant="secondary" onClick={() => setIsEditing(true)} title="Edit version" />
                    ) : (
                      <div className="tw-flex tw-flex-row tw-gap-2">
                        <PSButton id="btn-detail-cancel" type="button" variant="outline" onClick={() => setIsEditing(false)} title="Cancel" />
                        <PSButton
                          id="btn-detail-submit"
                          type="submit"
                          variant="primary"
                          title="Save"
                          isLoading={updateResult.isLoading}
                          disabled={updateResult.isLoading}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Description Section */}
              <div className="tw-bg-white tw-p-6 tw-rounded-xl tw-border tw-border-gray-200 tw-space-y-4">
                <div className="tw-flex tw-items-center tw-gap-2">
                  <BiInfoCircle className="tw-w-5 tw-h-5 tw-text-blue-600" />
                  <h2 className="tw-text-xl tw-font-semibold tw-text-gray-900">{t('marketplace/step3/description/title')}</h2>
                  <span className="tw-text-red-500">*</span>
                </div>

                <p className="tw-text-sm tw-text-gray-600">{t('marketplace/step3/description/hint')}</p>

                <BotResponseInput
                  title={''}
                  placeholder={t('marketplace/step3/description/placeholder')}
                  setValue={setValue}
                  getValues={getValues}
                  errors={errors}
                  name={'data.description'}
                  config={null}
                  showCallupFunctionsButton={false}
                  disabled={!isEditing}
                />

                <div className=" tw-mt-5 tw-self-stretch tw-grow tw-shrink tw-basis-0 tw-flex-col tw-justify-start tw-items-start tw-gap-3 tw-flex">
                  <div className="tw-self-stretch tw-text-gray-800 tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-7">
                    {t('DashboardSettingCustomizeChat/preview')}
                  </div>
                  <div
                    className={`tw-p-0.5 tw-bg-white tw-rounded-tl-2xl tw-rounded-tr-2xl tw-rounded-br-2xl tw-flex-col tw-justify-center tw-items-start tw-flex`}
                  >
                    <CustomReactMarkdown content={watch('data.description')} className="tw-text-gray-600 tw-px-2" />
                  </div>
                </div>
              </div>

              {/* Pricing Section */}
              <div className="tw-bg-white tw-p-6 tw-rounded-xl tw-border tw-border-gray-200 tw-space-y-4">
                <div className="tw-flex tw-items-center tw-gap-2">
                  <BiDollarCircle className="tw-w-5 tw-h-5 tw-text-yellow-600" />
                  <h2 className="tw-text-xl tw-font-semibold tw-text-gray-900">{t('marketplace/step3/pricing/title')}</h2>
                  <span className="tw-text-red-500">*</span>
                </div>

                <div className="tw-bg-blue-50 tw-p-4 tw-rounded-lg">
                  <p className="tw-text-sm tw-text-blue-800 tw-font-medium">
                    {t('marketplace/step3/pricing/range')}: ${type === 'video' ? '0 - $499' : '0 - $199'}
                  </p>
                  <p className="tw-text-xs tw-text-blue-600 tw-mt-1">
                    {type === 'video' ? t('marketplace/step3/pricing/videoNote') : t('marketplace/step3/pricing/textNote')}
                  </p>
                </div>

                <div>
                  <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">{t('marketplace/step3/pricing/label')}</label>
                  <div className="tw-relative tw-w-40">
                    <span className="tw-absolute tw-left-3 tw-top-1/2 tw-transform tw--translate-y-1/2 tw-text-gray-500 tw-font-medium">$</span>
                    <PSInput
                      min={0}
                      max={type === 'video' ? 499 : 199}
                      step={1}
                      {...register('price', {
                        required: t('marketplace/step3/pricing/validation/required'),
                        min: {
                          value: 0,
                          message: t('marketplace/step3/pricing/validation/minValue'),
                        },
                        max: {
                          value: type === 'video' ? 499 : 199,
                          message: t('marketplace/step3/pricing/validation/maxValue', {
                            max: type === 'video' ? 499 : 199,
                          }),
                        },
                        validate: value => {
                          if (value === '' || value === null || value === undefined) {
                            return t('marketplace/step3/pricing/validation/required');
                          }
                          const numValue = parseFloat(value);
                          if (isNaN(numValue)) {
                            return t('marketplace/step3/pricing/validation/invalidNumber');
                          }
                          return true;
                        },
                      })}
                      placeholder={t('marketplace/step3/pricing/placeholder')}
                      type={'number'}
                      className={`tw-border tw-border-solid tw-border-gray-300 tw-pl-8 tw-pr-4 tw-py-3 tw-text-base tw-border tw-border-gray-300 tw-rounded-lg tw-bg-white focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-opacity-50 ${
                        errors.price
                          ? 'tw-border-red-500 focus:tw-border-red-500 focus:tw-ring-red-500'
                          : 'tw-border-gray-300 focus:tw-border-blue-500 focus:tw-ring-blue-500'
                      }`}
                      disabled={!isEditing}
                    />
                  </div>
                  {errors?.price && <p className="tw-text-sm tw-text-red-600 tw-mt-1">{errors?.price?.message as string}</p>}
                </div>
              </div>
              {/* Video-specific fields */}
              {type === 'video' && (
                <div className="tw-space-y-6">
                  {/* Delivery delay */}
                  <div className="tw-bg-white tw-p-6 tw-rounded-xl tw-border tw-border-gray-200 tw-space-y-4">
                    <div className="tw-flex tw-items-center tw-gap-2">
                      <BiTime className="tw-w-5 tw-h-5 tw-text-purple-600" />
                      <h2 className="tw-text-xl tw-font-semibold tw-text-gray-900">{t('marketplace/step3/delivery/title')}</h2>
                      <span className="tw-text-red-500">*</span>
                    </div>

                    <p className="tw-text-sm tw-text-gray-600">{t('marketplace/step3/delivery/hint')}</p>

                    <div>
                      <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">{t('marketplace/step3/delivery/label')}</label>
                      <Controller
                        name="data.delivery_delay"
                        control={control}
                        render={({ field }) => (
                          <PSInput
                            {...field}
                            type="number"
                            min={1}
                            max={365}
                            placeholder={t('marketplace/step3/delivery/placeholder')}
                            className={`tw-border tw-border-solid tw-border-gray-300 tw-w-32 tw-px-4 tw-py-3 tw-text-base tw-border tw-border-gray-300 tw-rounded-lg tw-bg-white focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-opacity-50 ${
                              errors?.data && typeof errors.data === 'object' && 'delivery_delay' in errors.data
                                ? 'tw-border-red-500 focus:tw-border-red-500 focus:tw-ring-red-500'
                                : 'tw-border-gray-300 focus:tw-border-blue-500 focus:tw-ring-blue-500'
                            }`}
                            disabled={!isEditing}
                          />
                        )}
                      />
                      <p className="tw-text-xs tw-text-gray-500 tw-mt-1">{t('marketplace/step3/delivery/note')}</p>
                      {errors?.data && typeof errors.data === 'object' && 'delivery_delay' in errors.data && (
                        <p className="tw-text-sm tw-text-red-600 tw-mt-1">{(errors.data.delivery_delay as any).message as string}</p>
                      )}
                    </div>
                  </div>

                  {/* Promo video upload */}
                  <div className="tw-bg-white tw-p-6 tw-rounded-xl tw-border tw-border-gray-200 tw-space-y-4">
                    <div className="tw-flex tw-items-center tw-gap-2">
                      <BiVideo className="tw-w-5 tw-h-5 tw-text-red-600" />
                      <h2 className="tw-text-xl tw-font-semibold tw-text-gray-900">{t('marketplace/step3/video/title')}</h2>
                      <span className="tw-text-red-500">*</span>
                    </div>

                    <p className="tw-text-sm tw-text-gray-600">{t('marketplace/step3/video/hint')}</p>

                    <div className="tw-flex tw-flex-col sm:tw-flex-row tw-items-start tw-gap-4">
                      <PSInput
                        type="file"
                        hidden
                        accept="video/mp4,video/webm"
                        ref={videoInputRef}
                        onChange={e => handleUpload(e.target.files?.[0] || null, 'promotional_video')}
                        disabled={!isEditing}
                      />

                      <PSButton
                        id="btn-detail-upload-video"
                        variant="outline"
                        onClick={e => {
                          if (isEditing) videoInputRef.current?.click();
                        }}
                        type="button"
                        disabled={cdnUploadResults.isLoading || !isEditing}
                        className="tw-flex tw-items-center tw-gap-2"
                      >
                        {cdnUploadResults.isLoading ? <Spinner size="sm" /> : <BiPlus className="tw-w-4 tw-h-4" />}
                        {cdnUploadResults.isLoading ? t('LoadingComponent/text') : t('marketplace/step3/video/chooseButton')}
                      </PSButton>

                      {watch('data.promotional_video') && (
                        <div className="tw-flex tw-items-center tw-gap-3 tw-p-3 tw-bg-green-50 tw-border tw-border-green-200 tw-rounded-lg">
                          <div className="tw-w-12 tw-h-12 tw-bg-green-100 tw-rounded-lg tw-flex tw-items-center tw-justify-center">
                            <BiVideo className="tw-w-6 tw-h-6 tw-text-green-600" />
                          </div>
                          <div>
                            <p className="tw-text-sm tw-font-medium tw-text-green-800">{t('marketplace/step3/video/uploadSuccess')}</p>
                            <p className="tw-text-xs tw-text-green-600">{t('marketplace/step3/video/readyNote')}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Optional links */}
              <div className="tw-bg-white tw-p-6 tw-rounded-xl tw-border tw-border-gray-200 tw-space-y-6">
                <div className="tw-flex tw-items-center tw-gap-2">
                  <BiLink className="tw-w-5 tw-h-5 tw-text-indigo-600" />
                  <h2 className="tw-text-xl tw-font-semibold tw-text-gray-900">{t('marketplace/step3/links/title')}</h2>
                  <span className="tw-text-gray-400">({t('marketplace/step3/links/optional')})</span>
                </div>

                <p className="tw-text-sm tw-text-gray-600">{t('marketplace/step3/links/hint')}</p>

                <div className="tw-space-y-4">
                  <div>
                    <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">{t('marketplace/step3/links/youtube/label')}</label>
                    <Controller
                      name="data.youtube_link"
                      control={control}
                      render={({ field }) => (
                        <PSInput
                          {...field}
                          placeholder={t('marketplace/step3/links/youtube/placeholder')}
                          className={`tw-border tw-border-solid tw-border-gray-300 tw-px-4 tw-py-3 tw-text-base tw-border tw-border-gray-300 tw-rounded-lg tw-bg-white focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-opacity-50 ${
                            errors?.data && typeof errors.data === 'object' && 'youtube_link' in errors.data
                              ? 'tw-border-red-500 focus:tw-border-red-500 focus:tw-ring-red-500'
                              : 'tw-border-gray-300 focus:tw-border-blue-500 focus:tw-ring-blue-500'
                          }`}
                          disabled={!isEditing}
                        />
                      )}
                    />
                    {errors?.data && typeof errors.data === 'object' && 'youtube_link' in errors.data && (
                      <p className="tw-text-sm tw-text-red-600 tw-mt-1">{(errors.data.youtube_link as any).message as string}</p>
                    )}
                  </div>

                  <div>
                    <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">{t('marketplace/step3/links/linkedin/label')}</label>
                    <Controller
                      name="data.linkedin_link"
                      control={control}
                      render={({ field }) => (
                        <PSInput
                          {...field}
                          placeholder={t('marketplace/step3/links/linkedin/placeholder')}
                          className={`tw-border tw-border-solid tw-border-gray-300 tw-px-4 tw-py-3 tw-text-base tw-border tw-border-gray-300 tw-rounded-lg tw-bg-white focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-opacity-50 ${
                            errors?.data && typeof errors.data === 'object' && 'linkedin_link' in errors.data
                              ? 'tw-border-red-500 focus:tw-border-red-500 focus:tw-ring-red-500'
                              : 'tw-border-gray-300 focus:tw-border-blue-500 focus:tw-ring-blue-500'
                          }`}
                          disabled={!isEditing}
                        />
                      )}
                    />
                    {errors?.data && typeof errors.data === 'object' && 'linkedin_link' in errors.data && (
                      <p className="tw-text-sm tw-text-red-600 tw-mt-1">{(errors.data.linkedin_link as any).message as string}</p>
                    )}
                  </div>
                </div>
              </div>

              <Step2 editData={getValues()} disabled={!isEditing} />
            </div>

            {/* Sidebar */}
            <div className="lg:tw-col-span-1 tw-space-y-8">
              {/* Rejection Messages */}
              {data.status === 'REJECTED' &&
                data?.additional_data?.messages &&
                Array.isArray(data.additional_data.messages) &&
                data.additional_data.messages.length > 0 && (
                  <div className="tw-bg-red-50 tw-border tw-border-red-200 tw-rounded-xl tw-shadow-md tw-p-6 tw-mb-4">
                    <div className="tw-flex tw-items-center tw-gap-3 tw-mb-2">
                      <FaInfo className="tw-text-red-600 tw-text-xl" />
                      <h2 className="tw-text-lg tw-font-bold tw-text-red-800">{t('packageDetail/rejectionReasons')}</h2>
                    </div>
                    <ul className="tw-list-disc tw-ml-6 tw-text-red-700">
                      {data.additional_data.messages.length > 0 && <li>{data.additional_data.messages[data.additional_data.messages.length - 1]}</li>}
                    </ul>
                  </div>
                )}

              {/* Media Upload Section */}
              <div className="tw-bg-white tw-p-6 tw-rounded-xl tw-border tw-border-gray-200 tw-space-y-6">
                <div className="tw-flex tw-items-center tw-gap-2">
                  <BiImage className="tw-w-5 tw-h-5 tw-text-green-600" />
                  <h2 className="tw-text-xl tw-font-semibold tw-text-gray-900">{t('marketplace/step3/image/title')}</h2>
                  <span className="tw-text-red-500">*</span>
                </div>

                <p className="tw-text-sm tw-text-gray-600">{t('marketplace/step3/image/hint')}</p>

                <div className="tw-flex tw-flex-col sm:tw-flex-row tw-items-start tw-gap-4">
                  <PSInput
                    type="file"
                    hidden
                    accept="image/png,image/jpeg,image/webp"
                    ref={fileInputRef}
                    onChange={e => handleUpload(e.target.files?.[0] || null, 'image')}
                    disabled={!isEditing}
                  />

                  <PSButton
                    id="btn-detail-upload-file"
                    variant="outline"
                    onClick={e => {
                      if (isEditing) fileInputRef.current?.click();
                    }}
                    type="button"
                    disabled={cdnUploadResults.isLoading || !isEditing}
                    className="tw-flex tw-items-center tw-gap-2"
                  >
                    {cdnUploadResults.isLoading ? <Spinner size="sm" /> : <BiPlus className="tw-w-4 tw-h-4" />}
                    {cdnUploadResults.isLoading ? t('LoadingComponent/text') : t('marketplace/step3/image/chooseButton')}
                  </PSButton>
                </div>
              </div>
              {/* Image Card */}
              {watch('data.image') && (
                <div className="tw-bg-white tw-rounded-xl tw-shadow-md tw-overflow-hidden tw-h-[120px]">
                  <img
                    src={watch('data.image')}
                    alt={name}
                    className="tw-w-full tw-object-cover  tw-h-[120px] tw-self-stretch tw-bg-primary tw-rounded-lg tw-flex tw-flex-col tw-justify-center tw-items-center "
                  />
                </div>
              )}

              {/* Details Card */}
              <div className="tw-bg-white tw-rounded-xl tw-shadow-md tw-p-6">
                <h3 className="tw-text-xl tw-font-bold tw-text-gray-800 tw-mb-4">{t('packageDetail/details')}</h3>
                <ul className="tw-space-y-3 tw-text-gray-700">
                  <li className="tw-flex tw-items-center tw-gap-3">
                    <FaTag className="tw-text-gray-400" />
                    <span>
                      {t('packageDetail/version')} <span className="tw-font-semibold">{version}</span>
                    </span>
                  </li>
                  <li className="tw-flex tw-items-center tw-gap-3">
                    <FaBox className="tw-text-gray-400" />
                    <span>
                      {t('packageDetail/type')}{' '}
                      <span className="tw-font-semibold tw-capitalize">
                        {type == 'VIDEOBOT' ? t('marketplace/step1/types/video/label') : t('marketplace/step1/types/text/label')}
                      </span>
                    </span>
                  </li>
                  <li className="tw-flex tw-items-center tw-gap-3">
                    <FaCalendarAlt className="tw-text-gray-400" />
                    <span>
                      {t('packageDetail/created')} <span className="tw-font-semibold">{new Date(creation_date_time).toLocaleDateString()}</span>
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </form>
      </FormProvider>
    </div>
  );
}

export default WrappedErrorBoundary(PackageDetail);
